import { Directive, Input, OnDestroy, OnInit } from '@angular/core';
import { BreakpointObserver, Breakpoints } from '@angular/cdk/layout';
import { MatGridList } from '@angular/material/grid-list';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';

export interface IResponsiveColumnsMap {
  xs?: number;
  sm?: number;
  md?: number;
  lg?: number;
  xl?: number;
}

const BREAKPOINT_ALIASES: Array<[keyof IResponsiveColumnsMap, string]> = [
  ['xs', Breakpoints.XSmall],
  ['sm', Breakpoints.Small],
  ['md', Breakpoints.Medium],
  ['lg', Breakpoints.Large],
  ['xl', Breakpoints.XLarge],
];

// Usage: <mat-grid-list [responsiveCols]="{xs: 2, sm: 2, md: 4, lg: 6, xl: 8}">
@Directive({
    selector: '[responsiveCols]',
    standalone: false
})
export class ResponsiveColsDirective implements OnInit, OnDestroy {
  private countBySize: IResponsiveColumnsMap = { xs: 2, sm: 2, md: 4, lg: 6, xl: 8 };

  public get cols(): IResponsiveColumnsMap {
    return this.countBySize;
  }

  @Input('responsiveCols')
  public set cols(map: IResponsiveColumnsMap) {
    if (map && (typeof map === 'object')) {
      this.countBySize = map;
    }
  }

  destroy$ = new Subject<void>();

  public constructor(
    private grid: MatGridList,
    private breakpointObserver: BreakpointObserver,
  ) {}

  public ngOnInit(): void {
    this.applyColsForCurrentBreakpoint();

    this.breakpointObserver
      .observe(BREAKPOINT_ALIASES.map(([, query]) => query))
      .pipe(takeUntil(this.destroy$))
      .subscribe(() => this.applyColsForCurrentBreakpoint());
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  private applyColsForCurrentBreakpoint(): void {
    for (const [alias, query] of BREAKPOINT_ALIASES) {
      if (this.breakpointObserver.isMatched(query) && this.countBySize[alias] !== undefined) {
        this.grid.cols = this.countBySize[alias];
        return;
      }
    }
  }
}
